import React from 'react'
import logo from './assets/images/logo.png'

/**
 * Full-page screen shown instead of the call: a broken or expired join link, a call that has
 * already ended, or the hand-off back to the doctor app after the call.
 *
 * Styled after the Tiryaq doctor app (login / status pages): white page with a soft teal glow,
 * a white bordered card, teal accents and the same "Help & Support" header link. A link
 * problem gets a two-column card with the support illustration, like the doctor app's login.
 * Icons are Lucide outlines (ISC licence), inlined so the kit needs no icon dependency.
 */

/** Sentinel App.tsx sets while it sends the doctor back to the consultation page. */
const REDIRECTING_MESSAGE = 'Redirecting...'
/** Same destination as the doctor app header's "Help & Support" link. */
const HELP_URL = 'https://tiryaqapp.com/'
/** Served from example/public. PUBLIC_URL keeps it working under the "." homepage. */
const LINK_ISSUE_ILLUSTRATION = `${process.env.PUBLIC_URL}/meeting-link-support-pro.svg`

type StatusVariant = 'error' | 'ended' | 'redirecting'

const getVariant = (message: string): StatusVariant => {
  if (message === REDIRECTING_MESSAGE) return 'redirecting'
  // Every "call is over" message App.tsx produces contains "ended".
  if (message.includes('ended')) return 'ended'
  return 'error'
}

const Icon = ({ children }: { children: React.ReactNode }) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    {children}
  </svg>
)

const PhoneOffIcon = () => (
  <Icon>
    <path d='M10.1 13.9a14 14 0 0 0 3.732 2.668 1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2 18 18 0 0 1-12.728-5.272' />
    <path d='M22 2 2 22' />
    <path d='M4.76 13.582A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 .244.473' />
  </Icon>
)

const InfoIcon = () => (
  <Icon>
    <circle cx='12' cy='12' r='10' />
    <path d='M12 16v-4' />
    <path d='M12 8h.01' />
  </Icon>
)

const HelpIcon = () => (
  <Icon>
    <circle cx='12' cy='12' r='10' />
    <path d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' />
    <path d='M12 17h.01' />
  </Icon>
)

interface StatusScreenProps {
  /** The error / status text App.tsx put in its `error` state. */
  message: string
}

const StatusScreen = ({ message }: StatusScreenProps) => {
  const variant = getVariant(message)

  return (
    <div className='app-shell'>
      {/* app-header--page: the doctor app's page header (see App.css). */}
      <header className='app-header app-header--page'>
        <img className='app-logo' src={logo} alt='Tiryaq' />
        <a
          className='app-help-link'
          href={HELP_URL}
          target='_blank'
          rel='noopener noreferrer'
        >
          <HelpIcon />
          <span>Help &amp; Support</span>
        </a>
      </header>

      <main className='status-screen'>
        <section
          className={`status-card status-card--${variant}`}
          // An error is announced immediately; the end / redirect notices politely.
          role={variant === 'error' ? 'alert' : 'status'}
        >
          {variant === 'error' ? (
            // The illustration already carries the warning, so no icon badge here.
            // Decorative: the heading and message beside it say the same thing.
            <div className='status-illustration'>
              <img
                src={LINK_ISSUE_ILLUSTRATION}
                alt=''
                width={800}
                height={560}
                decoding='async'
              />
            </div>
          ) : (
            <div className={`status-icon status-icon--${variant}`}>
              {variant === 'redirecting' ? (
                <span className='status-spinner' aria-hidden='true' />
              ) : (
                <PhoneOffIcon />
              )}
            </div>
          )}

          <div className='status-body'>
            <h1 className='status-title'>
              {variant === 'error' ? 'Unable to Join Meeting' : 'Call Ended'}
            </h1>
            <p className='status-message'>
              {variant === 'redirecting'
                ? 'Redirecting to consultation page...'
                : message}
            </p>

            {variant === 'error' && (
              <div className='status-help'>
                <InfoIcon />
                <p>
                  Please check your meeting link and try again. If the problem
                  persists, contact support.
                </p>
              </div>
            )}

            {variant === 'ended' && (
              <p className='status-note'>Thank you for using Tiryaq.</p>
            )}
          </div>
        </section>
      </main>

      <footer className='app-footer'>
        © {new Date().getFullYear()} Tiryaq — One Journey. Complete Care
      </footer>
    </div>
  )
}

export default StatusScreen
